import { useEffect, useMemo, useRef, useState } from "react";
import { faqQuestions } from "../../data/faqs";
import "../../styles/components/_accordion.scss";

// ─── Icons ────────────────────────────────────────────────────────────────────
const IconPlus = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        aria-hidden="true"
    >
        <path
            d="M8 3v10M3 8h10"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
        />
    </svg>
);

const IconMinus = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        aria-hidden="true"
    >
        <path
            d="M3 8h10"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
        />
    </svg>
);

// ─── Single accordion item ────────────────────────────────────────────────────
function AccordionItem({
    id,
    question,
    description,
    isActive,
    onToggle,
    index,
}) {
    const innerRef = useRef(null);
    const [height, setHeight] = useState("0px");

    useEffect(() => {
        if (!innerRef.current) return;
        setHeight(isActive ? `${innerRef.current.scrollHeight}px` : "0px");
    }, [isActive, description]);

    const contentId = `faq-panel-${id}`;
    const buttonId = `faq-btn-${id}`;

    return (
        <div
            className={`faq-item${isActive ? " faq-item--open" : ""}`}
            style={{ "--item-index": index }}
        >
            <h3 className="faq-item__heading">
                <button
                    type="button"
                    id={buttonId}
                    className="faq-item__trigger"
                    aria-expanded={isActive}
                    aria-controls={contentId}
                    onClick={onToggle}
                >
                    <span className="faq-item__question">{question}</span>
                    <span className="faq-item__icon" aria-hidden="true">
                        {isActive ? <IconMinus /> : <IconPlus />}
                    </span>
                </button>
            </h3>

            <div
                id={contentId}
                role="region"
                aria-labelledby={buttonId}
                className="faq-item__body"
                style={{ maxHeight: height }}
            >
                <div ref={innerRef} className="faq-item__body-inner">
                    <div
                        className="faq-item__content"
                        dangerouslySetInnerHTML={{ __html: description }}
                    />
                </div>
            </div>
        </div>
    );
}

// ─── Accordion ────────────────────────────────────────────────────────────────
export default function Accordion({
    page,
    ids,
    categories,
    showCategories = true,
    defaultOpenId = null,
    data = faqQuestions,
}) {
    const [activeId, setActiveId] = useState(defaultOpenId);

    const filtered = useMemo(() => {
        let list = Array.isArray(data) ? data : [];
        if (page) list = list.filter((q) => q.page === page);
        if (ids?.length) {
            const s = new Set(ids);
            list = list.filter((q) => s.has(q.id));
        }
        if (categories?.length) {
            const s = new Set(categories.map((c) => c.toLowerCase()));
            list = list.filter((q) => s.has(String(q.category).toLowerCase()));
        }
        return list;
    }, [data, page, ids, categories]);

    const groups = useMemo(() => {
        if (!showCategories) return [{ key: "all", items: filtered }];
        const map = new Map();
        filtered.forEach((item) => {
            const key = item.category || "Other";
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(item);
        });
        return Array.from(map.entries()).map(([key, items]) => ({
            key,
            items,
        }));
    }, [filtered, showCategories]);

    // Global index for staggered animation across all groups
    let globalIndex = 0;

    return (
        <div className="faq-accordion">
            {groups.map((group) => (
                <div className="faq-group" key={group.key}>
                    {showCategories && group.key !== "all" && (
                        <p className="faq-group__label">{group.key}</p>
                    )}
                    <div className="faq-group__items">
                        {group.items.map((item, i) => {
                            const safeId =
                                item.id ??
                                `${String(item.category || "other")
                                    .toLowerCase()
                                    .replace(/\s+/g, "-")}-${i}`;
                            const idx = globalIndex++;
                            return (
                                <AccordionItem
                                    key={safeId}
                                    id={safeId}
                                    question={item.question}
                                    description={item.description}
                                    isActive={activeId === safeId}
                                    onToggle={() =>
                                        setActiveId(
                                            activeId === safeId ? null : safeId
                                        )
                                    }
                                    index={idx}
                                />
                            );
                        })}
                    </div>
                </div>
            ))}
        </div>
    );
}
